import type { GameSave } from "../types/save";
import { createDefaultGameSave, validateGameSave } from "../core/validation";

export interface SaveAdapter {
  load(): Promise<GameSave | null>;
  save(save: GameSave): Promise<void>;
  clear(): Promise<void>;
}

export type SaveMigrationStep = (save: GameSave) => GameSave;

export class SaveMigratorRegistry {
  private readonly steps = new Map<number, SaveMigrationStep>();

  public register(fromVersion: number, step: SaveMigrationStep): void {
    this.steps.set(fromVersion, step);
  }

  public migrate(save: GameSave, targetVersion: number): GameSave {
    let current = save;

    while (current.version < targetVersion) {
      const step = this.steps.get(current.version);
      if (step === undefined) {
        throw new Error(
          `Unsupported save version ${current.version}: no migration path to version ${targetVersion}`,
        );
      }
      current = step(current);
    }

    if (current.version > targetVersion) {
      throw new Error(
        `Unsupported save version ${current.version}: cannot downgrade to version ${targetVersion}`,
      );
    }

    return current;
  }
}

export interface SaveManagerContract {
  load(): Promise<GameSave>;
  save(save: GameSave): Promise<void>;
  clear(): Promise<void>;
}

export class MemorySaveAdapter implements SaveAdapter {
  private storedSave: GameSave | null = null;

  public constructor(initialSave: GameSave | null = null) {
    this.storedSave = initialSave;
  }

  public async load(): Promise<GameSave | null> {
    return this.storedSave;
  }

  public async save(save: GameSave): Promise<void> {
    this.storedSave = save;
  }

  public async clear(): Promise<void> {
    this.storedSave = null;
  }
}

export class LocalStorageSaveAdapter implements SaveAdapter {
  public constructor(private readonly storageKey: string) {}

  public async load(): Promise<GameSave | null> {
    const raw = localStorage.getItem(this.storageKey);
    if (raw === null) return null;

    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) return null;
      return parsed as GameSave;
    } catch {
      return null;
    }
  }

  public async save(save: GameSave): Promise<void> {
    localStorage.setItem(this.storageKey, JSON.stringify(save));
  }

  public async clear(): Promise<void> {
    localStorage.removeItem(this.storageKey);
  }
}

export class SaveManager implements SaveManagerContract {
  private readonly currentVersion = 4;

  public constructor(
    private readonly adapter: SaveAdapter,
    private readonly registry: SaveMigratorRegistry = new SaveMigratorRegistry(),
  ) {
    this.registry.register(1, (save) => ({
      ...save,
      version: 2,
      progression: {
        ...save.progression,
        noteUnlocked: false,
      },
    }));

    this.registry.register(2, (save) => {
      const completedGroupsByLevel: Record<string, readonly string[]> = {};
      const chapterRegex = /^ch(\d+)-/;

      for (const levelId of Object.keys(save.collectibles.byLevel)) {
        const match = chapterRegex.exec(levelId);
        if (match) {
          completedGroupsByLevel[levelId] = [match[1]!];
        }
      }

      return {
        ...save,
        version: 3,
        collectibles: {
          ...save.collectibles,
          completedGroupsByLevel,
        },
      };
    });

    this.registry.register(3, (save) => ({
      ...save,
      version: 4,
      triggeredBreadcrumbs: [],
    }));
  }

  public async load(): Promise<GameSave> {
    const raw = await this.adapter.load();

    if (raw === null) {
      return createDefaultGameSave();
    }

    const validated = validateGameSave(raw);

    if (validated.version === this.currentVersion) {
      return validated;
    }

    const migrated = this.registry.migrate(validated, this.currentVersion);
    return validateGameSave(migrated);
  }

  public async save(save: GameSave): Promise<void> {
    const validated = validateGameSave(save);

    if (validated.version !== this.currentVersion) {
      throw new Error(
        `Cannot save with version ${validated.version}: expected version ${this.currentVersion}`,
      );
    }

    await this.adapter.save(validated);
  }

  public async clear(): Promise<void> {
    await this.adapter.clear();
  }
}
